import { CHANNEL_TYPE_MAP, DriverSessionAverageRow, StintAverageRow } from './types';
import { formatLapSeconds, parseLapTimeSeconds } from './analytics/lapTime';

export class TeamInfo {
  public id: string;
  public kartNumber: string = '';
  public position: string = '';
  public driver: string = '';
  public teamName: string = '';
  public category: string = '';
  public sector1: string = '';
  public sector2: string = '';
  public sector3: string = '';
  public lastLap: string = '';
  public gap: string = '';
  public interval: string = '';
  public bestLap: string = '';
  public onTrackTime: string = '';
  public pitCount: string = '';
  public inBox: boolean = false;
  public boxTimeRemaining: string = '';
  public pitEntryTime: number | null = null;
  public pitDuration: string = '';
  public previousValues: Map<string, string> = new Map();
  public lastLapTimes: number[] = [];
  public lapCount: number = 1;
  public stintAverages: StintAverageRow[] = [];
  public driverSessionAverages: DriverSessionAverageRow[] = [];

  constructor(id: string) {
    this.id = id;
  }

  updateFromChannel(channel: string, value: string): void {
    const field = CHANNEL_TYPE_MAP.get(channel);
    if (field) {
      // Store previous value before updating
      this.previousValues.set(field, (this as any)[field]);
      (this as any)[field] = value;
    }
  }

  hasChanged(field: string): boolean {
    return this.previousValues.has(field) && this.previousValues.get(field) !== (this as any)[field];
  }

  updatePitDuration(): void {
    if (this.inBox && this.pitEntryTime) {
      const duration = Math.floor((Date.now() - this.pitEntryTime) / 1000);
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      this.pitDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else {
      this.pitDuration = '';
    }
  }

  updateLapTime(lapTime: string): void {
    if (!lapTime || lapTime === '-' || lapTime === '--:--.---') return;
    
    // Convert lap time to seconds if it's in MM:SS.mmm format
    const timeInSeconds = this.convertLapTimeToSeconds(lapTime);
    if (timeInSeconds === null) return;

    if (this.lapCount >= 1) {
      this.lastLapTimes.unshift(timeInSeconds); // Add to beginning of array
      if (this.lastLapTimes.length > 10) {
        this.lastLapTimes.pop(); // Keep a rolling window of the last 10 laps
      }
    }
    this.lapCount++;
  }

  clearLapTimes(): void {
    this.lastLapTimes = [];
    this.lapCount = 0;
  }

  convertLapTimeToSeconds(lapTime: string): number | null {
    return parseLapTimeSeconds(lapTime);
  }

  getAverageLapTime(): string {
    return this.formatAverageOverLast(5);
  }

  getAverageLapTime10(): string {
    return this.formatAverageOverLast(10);
  }

  getConsistency5(): string {
    return this.formatConsistencyOverLast(5);
  }

  getConsistency10(): string {
    return this.formatConsistencyOverLast(10);
  }

  private formatConsistencyOverLast(n: number): string {
    const window = this.lastLapTimes.slice(0, n);
    if (window.length < 2) return '';
    const avg = window.reduce((a, b) => a + b, 0) / window.length;
    const variance = window.reduce((acc, t) => acc + (t - avg) ** 2, 0) / window.length;
    return Math.sqrt(variance).toFixed(3);
  }

  private formatAverageOverLast(n: number): string {
    if (this.lastLapTimes.length === 0) return '';

    const window = this.lastLapTimes.slice(0, n);
    const sum = window.reduce((acc, time) => acc + time, 0);
    const avg = sum / window.length;
    return formatLapSeconds(avg);
  }


  /**
   * "Clean" average lap over the last `n` laps with outliers removed — laps more
   * than 5% off the median (traffic / pit in-out laps) are dropped, leaving a
   * representative pace. Falls back to the full window if too few clean laps.
   */
  getCleanAverageLap(n: number = 10): string {
    const window = this.lastLapTimes.slice(0, n).filter((t) => t > 0);
    if (window.length < 3) return '';
    const sorted = [...window].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const lo = median * 0.95;
    const hi = median * 1.05;
    const clean = window.filter((t) => t >= lo && t <= hi);
    const use = clean.length >= 2 ? clean : window;
    const avg = use.reduce((a, b) => a + b, 0) / use.length;
    return formatLapSeconds(avg);
  }

  /**
   * Consistency rating from the coefficient of variation (std dev / mean) over
   * the last `n` laps — track-independent so it's comparable across kart pace.
   * Returns a label + 0-100 score (higher = more consistent), or null if too few laps.
   */
  getConsistencyRating(
    n: number = 10,
  ): { label: 'excellent' | 'good' | 'fair' | 'poor'; score: number; cv: number } | null {
    const window = this.lastLapTimes.slice(0, n).filter((t) => t > 0);
    if (window.length < 3) return null;
    const avg = window.reduce((a, b) => a + b, 0) / window.length;
    if (avg <= 0) return null;
    const variance =
      window.reduce((acc, t) => acc + (t - avg) ** 2, 0) / window.length;
    const cv = Math.sqrt(variance) / avg;
    const label =
      cv < 0.006 ? 'excellent' : cv < 0.012 ? 'good' : cv < 0.02 ? 'fair' : 'poor';
    const score = Math.max(0, Math.min(100, Math.round(100 - cv * 100 * 25)));
    return { label, score, cv };
  }

  toString(): string {
    return `Team ${this.id} - Kart: ${this.kartNumber}, Pos: ${this.position}, Driver: ${this.driver}, Last Lap: ${this.lastLap}, Best Lap: ${this.bestLap}, Gap: ${this.gap}, Interval: ${this.interval}, Sectors: ${this.sector1}/${this.sector2}/${this.sector3}, On Track: ${this.onTrackTime}, Pits: ${this.pitCount}, Box: ${this.inBox ? 'IN' : 'OUT'}${this.inBox ? ` (${this.boxTimeRemaining} / ${this.pitDuration})` : ''}`;
  }
} 